import React from "react";
import { useNavigate } from "react-router-dom";
import { getUpgradePlan, PLAN_DISPLAY_NAMES } from "../../lib/planGating";

/**
 * UpgradePrompt — tier-aware upgrade banner
 *
 * Props:
 *   feature     {string}  – short name of the locked feature
 *   message     {string}  – optional custom message override
 *   currentPlan {string}  – current plan name (default: "free")
 *   compact     {boolean} – renders a smaller inline version
 *   className   {string}  – extra Tailwind classes
 */
export default function UpgradePrompt({ feature, message, currentPlan = "free", compact = false, className = "" }) {
  const navigate = useNavigate();
  const nextPlan = getUpgradePlan(currentPlan);
  const nextPlanName = nextPlan ? (PLAN_DISPLAY_NAMES[nextPlan] || nextPlan) : "a higher plan";

  const body =
    message ||
    (feature
      ? `${feature} is available on the ${nextPlanName} plan.`
      : `This feature is available on the ${nextPlanName} plan.`);

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1 ${className}`}>
        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        {body}{" "}
        <button
          onClick={() => navigate("/settings?tab=plan")}
          className="font-semibold underline hover:no-underline text-amber-800"
        >
          Upgrade to {nextPlanName}
        </button>
      </span>
    );
  }

  return (
    <div className={`rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 ${className}`}>
      <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
        <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l14 9-14 9V3z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-800">Upgrade Required</p>
        <p className="text-sm text-amber-700 mt-0.5">{body}</p>
      </div>
      <button
        onClick={() => navigate("/settings?tab=plan")}
        className="flex-shrink-0 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 active:bg-amber-700 transition-colors rounded-lg px-3 py-1.5"
      >
        Upgrade
      </button>
    </div>
  );
}
